import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Sparkles, BookOpen } from 'lucide-react';
export function SolutionSection() {
  return <section className="py-16 px-4 bg-ice-dark relative overflow-hidden border-y border-gold/20" data-id="element-182">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#C5942A_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-183" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-184">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="bg-white p-10 md:p-16 rounded-3xl shadow-[0_10px_40px_-10px_rgba(27,42,74,0.1)] border border-gold/10 relative" data-id="element-185">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-ice-dark p-4 rounded-full border border-gold/20 shadow-lg" data-id="element-186">
            <Shield className="w-10 h-10 text-gold" data-id="element-187" />
          </div>

          <div className="text-center mt-6 mb-10" data-id="element-188">
            <div className="flex items-center justify-center gap-2 mb-4 text-gold" data-id="element-189">
              <Sparkles className="w-5 h-5" data-id="element-190" />
              <span className="font-body uppercase tracking-widest text-sm font-bold" data-id="element-191">
                La Solución
              </span>
              <Sparkles className="w-5 h-5" data-id="element-192" />
            </div>

            <h2 className="font-heading text-4xl md:text-5xl font-bold text-navy-dark mb-8" data-id="element-193">
              Descubre el Poder del{' '}
              <span className="text-blue" data-id="element-194">Escudo de Fe</span>
            </h2>

            {/* Book Image with "Libro digital" badge */}
            <div className="relative inline-block mb-10" data-id="element-195">
              <img src="https://cdn.magicpatterns.com/uploads/dhKPxStdFGsisRrHHJpGeR/heylloyd-image-1772637616792.png" alt="Escudo de Fe - Manual de Oración Diaria" className="w-64 md:w-80 mx-auto rounded-xl shadow-[0_15px_50px_-10px_rgba(15,29,54,0.4)] border border-gold/20" data-id="element-196" />
              <div className="absolute -top-3 -right-3 md:-right-6 bg-gradient-to-r from-gold-light to-gold-dark text-navy-dark px-4 py-1.5 rounded-full font-body text-xs font-bold uppercase tracking-wider shadow-lg flex items-center gap-1.5" data-id="element-197">
                <BookOpen className="w-3.5 h-3.5" data-id="element-198" />
                Libro digital
              </div>
            </div>

            <p className="font-body text-xl text-navy leading-relaxed mb-6" data-id="element-199">
              Presentamos el{' '}
              <strong className="text-blue" data-id="element-200">"Escudo de Fe"</strong>, una guía
              espiritual diseñada para fortalecer tu hogar contra los peligros
              externos.
            </p>
            <p className="font-body text-lg text-navy/80 leading-relaxed" data-id="element-201">
              Con esta poderosa herramienta de oración y protección divina,
              sentirás la paz mental que necesitas para enfrentar el día a día,
              sabiendo que tu familia está bajo un manto de seguridad superior.
            </p>
          </div>

          {/* Unique Mechanism Subsection */}
          <div className="pt-12 border-t border-gold/20" data-id="element-202">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-navy-dark mb-8 text-center" data-id="element-203">
              ¿Por qué el Escudo de Fe es diferente?
            </h3>

            <div className="space-y-8" data-id="element-204">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="flex gap-4" data-id="element-205">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold-light/20 border border-gold text-gold-dark flex items-center justify-center font-heading font-bold text-lg" data-id="element-206">
                  1
                </div>
                <div data-id="element-207">
                  <h4 className="font-heading font-bold text-xl text-navy-dark mb-2" data-id="element-208">
                    Oración de Sellamiento Específica
                  </h4>
                  <p className="font-body text-navy/80 leading-relaxed" data-id="element-209">
                    A diferencia de las oraciones genéricas, el Escudo de Fe
                    utiliza una técnica de sellamiento espiritual punto por
                    punto: puertas, ventanas, cada miembro de la familia. Cada
                    palabra fue elegida con un propósito divino específico.
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="flex gap-4" data-id="element-210">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold-light/20 border border-gold text-gold-dark flex items-center justify-center font-heading font-bold text-lg" data-id="element-211">
                  2
                </div>
                <div data-id="element-212">
                  <h4 className="font-heading font-bold text-xl text-navy-dark mb-2" data-id="element-213">
                    Activación Diaria en 5 Minutos
                  </h4>
                  <p className="font-body text-navy/80 leading-relaxed" data-id="element-214">
                    No necesitas horas de oración. El método de la Hermana
                    Teresa condensa décadas de sabiduría espiritual en un ritual
                    de protección que se activa en solo 5 minutos cada mañana,
                    creando un escudo que dura las 24 horas.
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex gap-4" data-id="element-215">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold-light/20 border border-gold text-gold-dark flex items-center justify-center font-heading font-bold text-lg" data-id="element-216">
                  3
                </div>
                <div data-id="element-217">
                  <h4 className="font-heading font-bold text-xl text-navy-dark mb-2" data-id="element-218">
                    Protección Comprobada por Miles
                  </h4>
                  <p className="font-body text-navy/80 leading-relaxed" data-id="element-219">
                    Más de 4,200 familias en México y toda Latinoamérica ya usan
                    este método. No es teoría: es una práctica espiritual con
                    resultados reales que las familias sienten desde el primer
                    día.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}